import { Area, AreaType } from '../area';
import { Load } from '../load';
import { LoadingSpace } from '../loading-space';

export function createPCTFreeSpaces(
  currentLoad: Load,
  loads: Load[],
  loadingSpace: LoadingSpace,
): Area[] {
  const loadsOnSpace: Load[] = getLoadsOnSpace(loads, currentLoad, loadingSpace);
  const pctMultiply: number = (100 - currentLoad.pctSupport) / 100;
  const temporaryAreas: Area[] = [];
  const loadsAreas: Area[] = loadsOnSpace
    .filter((l: Load) => {
      let isSame = false;
      if (l.stacking && l.stacked) {
        isSame = checkIfLoadIsDirectlyAbove(l, loads);
      }
      return l.stacking && !isSame;
    })
    .map((load: Load): Area => {
      return new Area(
        load.length,
        load.width,
        loadingSpace.height - (load.y + load.height),
        load.x,
        load.y + load.height,
        load.z,
        AreaType.pctBase,
      );
    });
  loadsAreas
    .filter((arr: Area): boolean => arr.y !== 0)
    .forEach((area: Area): void => {
      temporaryAreas.push(
        ...extendAreaForPct(currentLoad, pctMultiply, area, loadsOnSpace, loadingSpace),
      );
      temporaryAreas.push(area);
    });
  const cutOutAreas: Area[] = cutOutLoadsFromAreas(loadsOnSpace, mergeAreas(temporaryAreas));
  const numberOfLoops = 3;
  let mergedAreas: Area[] = cutOutAreas;
  for (let i = 0; i < numberOfLoops; i++) {
    mergedAreas = mergeAreas(mergedAreas);
  }
  return mergedAreas;
}

function cutOutLoadsFromAreas(loads: Load[], areas: Area[]): Area[] {
  const loadsOnTop: Load[] = loads.filter((load: Load) => {
    return load.y !== 0;
  });
  const areasOverlappingLoads: Area[] = areas.filter((area: Area): boolean => {
    return loadsOnTop.some((load: Load): boolean => {
      return (
        area.x < load.x + load.width &&
        area.x + area.width > load.x &&
        area.z < load.z + load.length &&
        area.z + area.length > load.z &&
        area.y === load.y
      );
    });
  });
  const areasNotOverlappingLoads: Area[] = areas.filter((area: Area): boolean => {
    return !loadsOnTop.some((load: Load): boolean => {
      return (
        area.x < load.x + load.width &&
        area.x + area.width > load.x &&
        area.z < load.z + load.length &&
        area.z + area.length > load.z &&
        area.y === load.y
      );
    });
  });
  const tempAreas: Area[] = [];
  areasOverlappingLoads.forEach((area: Area): void => {
    tempAreas.push(...cutOutArea(area, loadsOnTop));
  });
  tempAreas.push(...areasNotOverlappingLoads);
  return tempAreas;
}

function cutOutArea(area: Area, loads: Load[]): Area[] {
  if (loads.length === 0) {
    return [area];
  }
  const cutOutAreas: Area[] = [];
  cutOutAreas.push(...splitOverlappingAreas(area, loads));
  const areasNotOverlappingLoads: Area[] = cutOutAreas.filter((area: Area): boolean => {
    return !loads.some((load: Load): boolean => {
      return (
        area.x < load.x + load.width &&
        area.x + area.width > load.x &&
        area.z < load.z + load.length &&
        area.z + area.length > load.z
      );
    });
  });
  return areasNotOverlappingLoads;
}

function splitOverlappingAreas(area1: Area, loads: Load[]): Area[] {
  let newAreas: Area[] = [];
  loads.forEach((load) => {
    newAreas.push(
      ...splitSingleOverlap(
        area1,
        new Area(load.length, load.width, load.height, load.x, load.y, load.z, AreaType.pctExtend),
      ),
    );
  });
  newAreas = newAreas.filter((area) => area.length > 0 && area.width > 0 && area.height > 0);
  return newAreas;
}

function splitSingleOverlap(area1: Area, area2: Area): Area[] {
  const areas: Area[] = [];
  if (area1.x < area2.x) {
    areas.push(
      new Area(
        area1.length,
        area2.x - area1.x,
        area1.height,
        area1.x,
        area1.y,
        area1.z,
        area1.type,
      ),
    );
  }
  if (area1.x + area1.width > area2.x + area2.width) {
    areas.push(
      new Area(
        area1.length,
        area1.x + area1.width - (area2.x + area2.width),
        area1.height,
        area2.x + area2.width,
        area1.y,
        area1.z,
        area1.type,
      ),
    );
  }
  if (area1.z < area2.z) {
    areas.push(
      new Area(area2.z - area1.z, area1.width, area1.height, area1.x, area1.y, area1.z, area1.type),
    );
  }
  if (area1.z + area1.length > area2.z + area2.length) {
    areas.push(
      new Area(
        area1.z + area1.length - (area2.z + area2.length),
        area1.width,
        area1.height,
        area1.x,
        area1.y,
        area2.z + area2.length,
        area1.type,
      ),
    );
  }
  if (area1.y < area2.y) {
    areas.push(
      new Area(area1.length, area1.width, area2.y - area1.y, area1.x, area1.y, area1.z, area1.type),
    );
  }
  if (area1.y + area1.height > area2.y + area2.height) {
    areas.push(
      new Area(
        area1.length,
        area1.width,
        area1.y + area1.height - (area2.y + area2.height),
        area1.x,
        area2.y + area2.height,
        area1.z,
        area1.type,
      ),
    );
  }
  return areas.filter(
    (area) =>
      area.length > 0 &&
      area.width > 0 &&
      area.height > 0 &&
      !(
        area.x < area2.x + area2.width &&
        area.x + area.width > area2.x &&
        area.z < area2.z + area2.length &&
        area.z + area.length > area2.z &&
        area.y < area2.y + area2.height &&
        area.y + area.height > area2.y
      ),
  );
}

function checkIfLoadIsDirectlyAbove(load: Load, loads: Load[]): boolean {
  return loads.some((l: Load) => {
    return isLoadDirectlyAbove(load, l);
  });
}

function isLoadDirectlyAbove(load1: Load, load2: Load): boolean {
  return (
    load2.pctSupport === null &&
    load1.x === load2.x &&
    load1.z === load2.z &&
    load1.width === load2.width &&
    load1.length === load2.length &&
    load1.y + load1.height === load2.y
  );
}

function getLoadsOnSpace(loads: Load[], currentLoad: Load, loadingSpace: LoadingSpace): Load[] {
  const filteredLoads = loads.filter((l: Load) => {
    const isCurrent = isCurrentLoad(l, currentLoad);
    const isOnTrailer = isLoadOnTrailer(l, loadingSpace);
    const isOnSemiTrailer = isLoadOnSemiTrailer(l, loadingSpace);
    return (isOnTrailer || isOnSemiTrailer) && !isCurrent;
  });
  return filteredLoads;
}

function isCurrentLoad(load: Load, currentLoad: Load): boolean {
  return load.id === currentLoad.id;
}

function isLoadOnTrailer(load: Load | Area, loadingSpace: LoadingSpace): boolean {
  return (
    load.x >= 0 && load.x <= loadingSpace.width && load.z >= 0 && load.z <= loadingSpace.length
  );
}

function isLoadOnSemiTrailer(load: Load | Area, loadingSpace: LoadingSpace): boolean {
  return (
    loadingSpace.width1 &&
    loadingSpace.height1 &&
    load.x >= 0 &&
    load.x <= loadingSpace.width1 &&
    load.z >= 0 &&
    load.z <= loadingSpace.length1
  );
}

function createSidesAreas(loadingSpace: LoadingSpace): Area[] {
  const gap = 2000;
  const virtualThickness = 10;
  const createArea = (
    length: number,
    width: number,
    height: number,
    x: number,
    y: number,
    z: number,
  ): Area => new Area(length, width, height, x, y, z, AreaType.pctHelper);

  const sideAreas: Area[] = [
    createArea(virtualThickness, loadingSpace.length, loadingSpace.height, 0, 0, -virtualThickness),
    createArea(loadingSpace.length, virtualThickness, loadingSpace.height, -virtualThickness, 0, 0),
    createArea(
      loadingSpace.length,
      virtualThickness,
      loadingSpace.height,
      loadingSpace.width,
      0,
      0,
    ),
    createArea(
      virtualThickness,
      loadingSpace.length,
      loadingSpace.height,
      0,
      0,
      loadingSpace.length + virtualThickness,
    ),
  ];

  if (loadingSpace.set) {
    sideAreas.push(
      createArea(
        virtualThickness,
        loadingSpace.length,
        loadingSpace.height,
        0,
        0,
        loadingSpace.length + gap - virtualThickness,
      ),
      createArea(
        loadingSpace.length,
        virtualThickness,
        loadingSpace.height,
        -virtualThickness,
        0,
        loadingSpace.length + gap,
      ),
      createArea(
        loadingSpace.length,
        virtualThickness,
        loadingSpace.height,
        loadingSpace.width,
        0,
        0,
      ),
      createArea(
        virtualThickness,
        loadingSpace.length,
        loadingSpace.height,
        0,
        0,
        loadingSpace.length + virtualThickness,
      ),
    );
  }

  return sideAreas;
}

function findClosestArea(areas: Area[], side = 'left'): Area[] {
  const sortByZ = (a: Area, b: Area) => b.z2() - a.z2();
  const sortByX = (a: Area, b: Area) => a.x2() - b.x2();
  switch (side) {
    case 'left':
      return areas.sort(sortByZ);
    case 'right':
      return areas.sort((a, b) => -sortByZ(a, b));
    case 'top':
      return areas.sort(sortByX);
    case 'bottom':
      return areas.sort((a, b) => -sortByX(a, b));
    default:
      return areas;
  }
}

function modCollidingArea(extendedArea: Area, areasFromLoads: Area[], side: string): Area {
  const isInCollision: Area[] = detectAreaLoadCollision(extendedArea, areasFromLoads);
  if (isInCollision.length === 0) {
    return extendedArea;
  } else {
    const collisionArea: Area[] = findClosestArea(isInCollision, side);
    return modifyCollidingAreaToFit(extendedArea, collisionArea[0], side);
  }
}

export function extendAreaOnAllSides(
  area: Area,
  currentLoad: Load,
  pctMultiply: number,
  areasFromLoads: Area[],
  loadingSpace: LoadingSpace,
): Area[] {
  const newLength = currentLoad.length * pctMultiply;
  const newWidth = currentLoad.width * pctMultiply;
  const createAndModifyArea = (
    length: number,
    width: number,
    height: number,
    x: number,
    y: number,
    z: number,
    side: string,
  ): Area => {
    let newArea: Area = new Area(
      length,
      width,
      height,
      x,
      y,
      z,
      side === 'corners' ? AreaType.pctCorner : AreaType.pctExtend,
    );
    newArea = modCollidingArea(newArea, areasFromLoads, side);
    return newArea;
  };

  const isAreaOnTrailer: boolean =
    area.x >= 0 && area.x <= loadingSpace.width && area.z >= 0 && area.z <= loadingSpace.length;
  const loadingSpaceHeight = !isAreaOnTrailer ? loadingSpace.height1 : loadingSpace.height;
  const areaHeight = loadingSpaceHeight - area.y;

  const leftArea = createAndModifyArea(
    newLength,
    area.width,
    areaHeight,
    area.x,
    area.y,
    area.z - newLength,
    'left',
  );
  const rightArea = createAndModifyArea(
    newLength,
    area.width,
    areaHeight,
    area.x,
    area.y,
    area.z + area.length,
    'right',
  );
  const topArea = createAndModifyArea(
    area.length,
    newWidth,
    areaHeight,
    area.x + area.width,
    area.y,
    area.z,
    'top',
  );
  const bottomArea = createAndModifyArea(
    area.length,
    newWidth,
    areaHeight,
    area.x - newWidth,
    area.y,
    area.z,
    'bottom',
  );
  // const cornerArea = createAndModifyArea(
  //   area.length + newLength,
  //   area.width + newWidth,
  //   areaHeight,
  //   area.x - newWidth / 2,
  //   area.y,
  //   area.z - newLength / 2,
  //   'corners',
  // );
  const cornerLeftArea = createAndModifyArea(
    newLength / 2,
    area.width + newWidth,
    areaHeight,
    area.x - newWidth / 2,
    area.y,
    area.z - newLength / 2,
    'left',
  );
  const cornerRightArea = createAndModifyArea(
    newLength / 2,
    area.width + newWidth,
    areaHeight,
    area.x - newWidth / 2,
    area.y,
    area.z + area.length,
    'right',
  );
  const cornerTopArea = createAndModifyArea(
    area.length + newLength,
    newWidth / 2,
    areaHeight,
    area.x + area.width,
    area.y,
    area.z - newLength / 2,
    'top',
  );
  const cornerBottomArea = createAndModifyArea(
    area.length + newLength,
    newWidth / 2,
    areaHeight,
    area.x - newWidth / 2,
    area.y,
    area.z - newLength / 2,
    'bottom',
  );

  const maxLengthCornerLeftRight = Math.min(
    cornerLeftArea.length + cornerRightArea.length + area.length,
    cornerTopArea.x + cornerTopArea.length - area.x,
  );
  const minXCornerLeftRight = Math.min(area.x, cornerBottomArea.x);
  const maxX = Math.min(
    minXCornerLeftRight + Math.max(cornerLeftArea.width, cornerRightArea.width),
    cornerTopArea.x + cornerTopArea.width,
  );

  const cornerLeftRightAreasMerged = new Area(
    maxLengthCornerLeftRight,
    maxX - minXCornerLeftRight,
    cornerLeftArea.height,
    minXCornerLeftRight,
    cornerLeftArea.y,
    Math.min(cornerLeftArea.z, cornerRightArea.z),
    AreaType.pctExtend,
  );

  const maxLengthCornerTopBottom = Math.min(
    cornerTopArea.length + cornerBottomArea.length + area.length,
    cornerLeftArea.z + cornerLeftArea.length - area.z,
  );

  const minZCornerTopBottom = Math.min(area.z, cornerLeftArea.z);

  const cornerTopBottomAreasMerged = new Area(
    maxLengthCornerTopBottom,
    cornerTopArea.width + area.width + cornerBottomArea.width,
    cornerTopArea.height,
    Math.min(cornerTopArea.x, cornerBottomArea.x),
    cornerTopArea.y,
    minZCornerTopBottom,
    AreaType.pctExtend,
  );
  const cornerAreasMerged: Area[] = [cornerTopBottomAreasMerged, cornerLeftRightAreasMerged];
  const leftRightCenterAreasMerged: Area[] = [leftArea, area, rightArea];
  const topBottomCenterAreasMerged: Area[] = [bottomArea, area, topArea];
  const mergedLeftRight = new Area(
    leftRightCenterAreasMerged[0].length +
      leftRightCenterAreasMerged[1].length +
      leftRightCenterAreasMerged[2].length,
    leftRightCenterAreasMerged[0].width,
    leftRightCenterAreasMerged[0].height,
    leftRightCenterAreasMerged[0].x,
    leftRightCenterAreasMerged[0].y,
    leftRightCenterAreasMerged[0].z,
    AreaType.pctExtend,
  );
  const mergedTopBottom = new Area(
    topBottomCenterAreasMerged[0].length,
    topBottomCenterAreasMerged[0].width +
      topBottomCenterAreasMerged[1].width +
      topBottomCenterAreasMerged[2].width,
    topBottomCenterAreasMerged[0].height,
    topBottomCenterAreasMerged[0].x,
    topBottomCenterAreasMerged[0].y,
    topBottomCenterAreasMerged[0].z,
    AreaType.pctExtend,
  );
  const nonCollidingAreas: Area[] = [mergedLeftRight, mergedTopBottom, ...cornerAreasMerged];
  return nonCollidingAreas;
}

export function mergeAreas(areas: Area[]): Area[] {
  let merged = true;

  while (merged) {
    merged = false;
    for (let i = 0; i < areas.length; i++) {
      for (let j = i + 1; j < areas.length; j++) {
        if (areAreasAdjacent(areas[i], areas[j])) {
          const mergedArea = tryMergeAreas(areas[i], areas[j]);
          if (mergedArea) {
            areas.splice(j, 1);
            areas[i] = mergedArea;
            merged = true;
            break;
          }
        }
      }
      if (merged) break;
    }
  }
  return [...areas];
}

function areAreasAdjacent(area1: Area, area2: Area): boolean {
  return (
    ((area1.x === area2.x + area2.width || area1.x + area1.width === area2.x) &&
      area1.y === area2.y &&
      area1.z === area2.z &&
      area1.height === area2.height &&
      area1.length === area2.length) ||
    ((area1.y === area2.y + area2.height || area1.y + area1.height === area2.y) &&
      area1.x === area2.x &&
      area1.z === area2.z &&
      area1.width === area2.width &&
      area1.length === area2.length) ||
    ((area1.z === area2.z + area2.length || area1.z + area1.length === area2.z) &&
      area1.x === area2.x &&
      area1.y === area2.y &&
      area1.width === area2.width &&
      area1.height === area2.height)
  );
}

function tryMergeAreas(area1: Area, area2: Area): Area | null {
  switch (true) {
    case area1.x === area2.x &&
      area1.width === area2.width &&
      area1.z === area2.z &&
      area1.length === area2.length:
      return new Area(
        area1.length,
        area1.width,
        area1.height,
        area1.x,
        Math.min(area1.y, area2.y),
        area1.z,
        area1.type,
      );

    case area1.y === area2.y &&
      area1.height === area2.height &&
      area1.z === area2.z &&
      area1.length === area2.length:
      return new Area(
        area1.length,
        area1.width + area2.width,
        area1.height,
        Math.min(area1.x, area2.x),
        area1.y,
        area1.z,
        area1.type,
      );

    case area1.x === area2.x &&
      area1.width === area2.width &&
      area1.y === area2.y &&
      area1.height === area2.height:
      return new Area(
        area1.length + area2.length,
        area1.width,
        area1.height,
        area1.x,
        area1.y,
        Math.min(area1.z, area2.z),
        area1.type,
      );

    default:
      return null;
  }
}

function createCoolerAreas(loadingSpace: LoadingSpace): Area[] {
  const coolerAreas: Area[] = [];
  if (loadingSpace.coolerTrailerWidth) {
    coolerAreas.push(
      new Area(
        loadingSpace.coolerTrailerLength,
        loadingSpace.coolerTrailerWidth,
        loadingSpace.coolerTrailerHeight,
        loadingSpace.width / 2 - loadingSpace.coolerTrailerWidth / 2,
        loadingSpace.coolerTrailerFloorHeight,
        0,
        AreaType.pctHelper,
      ),
    );
  }

  if (loadingSpace.coolerSemiTrailerWidth) {
    coolerAreas.push(
      new Area(
        loadingSpace.coolerSemiTrailerLength,
        loadingSpace.coolerSemiTrailerWidth,
        loadingSpace.coolerSemiTrailerHeight,
        loadingSpace.width / 2 - loadingSpace.coolerSemiTrailerWidth / 2,
        loadingSpace.coolerSemiTrailerFloorHeight,
        loadingSpace.length + loadingSpace.gap,
        AreaType.pctHelper,
      ),
    );
  }

  return coolerAreas;
}

function createWheelArchesAreas(loadingSpace: LoadingSpace): Area[] {
  const wheelArchesAreas: Area[] = [];

  if (loadingSpace.wheelTrailerWidth) {
    const width = loadingSpace.wheelTrailerWidth;
    const height = loadingSpace.wheelTrailerHeight;
    const length = loadingSpace.wheelTrailerLength;
    const doorDistance = loadingSpace.wheelTrailerDistance;
    const positionZ = loadingSpace.length - doorDistance - length;
    const positionX = 0;

    wheelArchesAreas.push(
      new Area(length, width, height, positionX, 0, positionZ, AreaType.pctHelper),
    );

    wheelArchesAreas.push(
      new Area(
        length,
        width,
        height,
        loadingSpace.width - width - positionX,
        0,
        positionZ,
        AreaType.pctHelper,
      ),
    );
  }

  if (loadingSpace.wheelSemiTrailerWidth) {
    const width = loadingSpace.wheelSemiTrailerWidth;
    const height = loadingSpace.wheelSemiTrailerHeight;
    const length = loadingSpace.wheelSemiTrailerLength;
    const positionZ =
      loadingSpace.length +
      loadingSpace.gap +
      loadingSpace.length1 -
      loadingSpace.wheelSemiTrailerDistance;
    const positionX = -(loadingSpace.width1 - loadingSpace.width) / 2;

    wheelArchesAreas.push(
      new Area(length, width, height, positionX, 0, positionZ, AreaType.pctHelper),
    );

    wheelArchesAreas.push(
      new Area(
        length,
        width,
        height,
        loadingSpace.width - width - positionX,
        0,
        positionZ,
        AreaType.pctHelper,
      ),
    );
  }

  return wheelArchesAreas;
}

export function extendAreaForPct(
  currentLoad: Load,
  pctMultiply: number,
  area: Area,
  loads: Load[],
  loadingSpace: LoadingSpace,
): Area[] {
  const sideAreas: Area[] = createSidesAreas(loadingSpace);
  const wheelArches: Area[] = createWheelArchesAreas(loadingSpace);
  const cooler: Area[] = createCoolerAreas(loadingSpace);
  const areasFromLoads: Area[] = loads.map((load: Load): Area => {
    return new Area(load.length, load.width, load.height, load.x, load.y, load.z, AreaType.pctBase);
  });
  areasFromLoads.push(...sideAreas, ...wheelArches, ...cooler);
  let areasExtended: Area[] = [];
  areasExtended = extendAreaOnAllSides(
    area,
    currentLoad,
    pctMultiply,
    areasFromLoads,
    loadingSpace,
  );
  return areasExtended.filter((area: Area): boolean => area.y !== 0);
}

export function modifyCollidingAreaToFit(area: Area, loadArea: Area, which: string): Area {
  let modifiedArea: Area = area;
  const endOfLoadArea: number = loadArea.z + loadArea.length;
  switch (which) {
    case 'right': {
      const truncatedSize: number =
        area.z + area.length > loadArea.z ? loadArea.z - area.z : area.length;
      modifiedArea = new Area(
        truncatedSize,
        area.width,
        area.height,
        area.x,
        area.y,
        area.z,
        area.type,
      );
      break;
    }
    case 'left': {
      const truncatedSizeLeft: number = area.z + area.length - endOfLoadArea;
      modifiedArea = new Area(
        truncatedSizeLeft,
        area.width,
        area.height,
        area.x,
        area.y,
        endOfLoadArea,
        area.type,
      );
      break;
    }
    case 'top': {
      const truncatedSizeTop: number =
        area.x + area.width > loadArea.x ? loadArea.x - area.x : area.width;
      modifiedArea = new Area(
        area.length,
        truncatedSizeTop,
        area.height,
        area.x,
        area.y,
        area.z,
        area.type,
      );
      break;
    }
    case 'bottom': {
      const truncatedSizeBottom: number = area.width - (loadArea.x + loadArea.width - area.x);
      const xPositionBottom: number = loadArea.x + loadArea.width;
      modifiedArea = new Area(
        area.length,
        truncatedSizeBottom,
        area.height,
        xPositionBottom,
        area.y,
        area.z,
        area.type,
      );
      break;
    }
    case 'corners': {
      const newAreas: Area[] = [];
      if (loadArea.z > area.z && loadArea.z < area.z + area.length) {
        newAreas.push(
          new Area(loadArea.z - area.z, area.width, area.height, area.x, area.y, area.z, area.type),
        );
      }
      if (
        loadArea.z + loadArea.length < area.z + area.length &&
        loadArea.z + loadArea.length > area.z
      ) {
        newAreas.push(
          new Area(
            area.z + area.length - (loadArea.z + loadArea.length),
            area.width,
            area.height,
            area.x,
            area.y,
            loadArea.z + loadArea.length,
            area.type,
          ),
        );
      }
      if (loadArea.x > area.x && loadArea.x < area.x + area.width) {
        newAreas.push(
          new Area(
            area.length,
            loadArea.x - area.x,
            area.height,
            area.x,
            area.y,
            area.z,
            area.type,
          ),
        );
      }
      if (
        loadArea.x + loadArea.width < area.x + area.width &&
        loadArea.x + loadArea.width > area.x
      ) {
        newAreas.push(
          new Area(
            area.length,
            area.x + area.width - (loadArea.x + loadArea.width),
            area.height,
            loadArea.x + loadArea.width,
            area.y,
            area.z,
            area.type,
          ),
        );
      }
      if (loadArea.x + loadArea.width > area.x && loadArea.x < area.x) {
        newAreas.push(
          new Area(
            area.length,
            area.x + area.width - loadArea.x,
            area.height,
            loadArea.x + loadArea.width,
            area.y,
            area.z,
            area.type,
          ),
        );
      }

      modifiedArea = newAreas.length > 0 ? newAreas[0] : area;
      break;
    }
  }
  return modifiedArea;
}

export function areAreasOverlapping(area1: Area, loadArea: Area): boolean {
  const threshold = 1;
  if (area1.y === loadArea.y) {
    return false;
  }
  const doesItOverlap =
    area1.x < loadArea.x + loadArea.width &&
    area1.x + area1.width > loadArea.x &&
    area1.y < loadArea.y + loadArea.height - threshold &&
    area1.y + area1.height > loadArea.y &&
    area1.z < loadArea.z + loadArea.length &&
    area1.z + area1.length > loadArea.z;
  return doesItOverlap;
}

export function detectAreaLoadCollision(area: Area, areas: Area[]): Area[] {
  const areasOverlapping: Area[] = [];
  areas.forEach((loadArea: Area): void => {
    const areAreasOverlappingArea: boolean = areAreasOverlapping(area, loadArea);
    if (areAreasOverlappingArea) {
      areasOverlapping.push(loadArea);
    }
  });
  return areasOverlapping;
}
